import React from "react";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import type { DailySalesDetail, MemberTabType } from "types/BusinessDashboard";
import type { MonthlyParams, SalesDetailParams } from "api/businessDashboard";
import {
  useSalesDetailQuery,
  useTargetDataQuery,
} from "queries/Business/Dashboard/useDashboardQueries";

import { ChartSkeleton } from "../chart-ui-kit";
import { RealChart } from "./real-chart";

dayjs.locale("ko");

export const UnitPriceChart = ({
  currentDate,
  detailApiParams,
  targetApiParams,
  currentMembersInnerTab,
}: {
  currentDate: dayjs.Dayjs;
  detailApiParams: SalesDetailParams;
  targetApiParams: MonthlyParams;
  currentMembersInnerTab: MemberTabType;
}) => {
  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useSalesDetailQuery(detailApiParams);
  const {
    data: targetData,
    isLoading: isTargetLoading,
    isError: isTargetError,
  } = useTargetDataQuery(targetApiParams, {
    enabled: true,
  });

  const isLoading = isDetailLoading || isTargetLoading;
  const isError = isDetailError || isTargetError;

  if (isLoading) return <ChartSkeleton title="객단가" />;
  if (isError || !detailData || !targetData) return <></>;

  const { charts } = (() => {
    const chartsResult = {
      periodUnitPrice: detailData.data.map((d: DailySalesDetail) => ({
        day: dayjs(d.referenceDate).date(),
        value: d.periodBasedUnitPrice.amount ?? 0,
        date: dayjs(d.referenceDate).format("YY.MM.DD"),
        changeRatePrevMonth:
          d.periodBasedUnitPrice.changeRateComparedToPreviousMonth,
      })),
      countUnitPrice: detailData.data.map((d: DailySalesDetail) => ({
        day: dayjs(d.referenceDate).date(),
        value: d.countBasedUnitPrice.amount ?? 0,
        date: dayjs(d.referenceDate).format("YY.MM.DD"),
        changeRatePrevMonth:
          d.countBasedUnitPrice.changeRateComparedToPreviousMonth,
      })),
    };

    return {
      charts: chartsResult,
    };
  })();

  // 1. 데이터 포인트 수
  const numDataPoints = charts.periodUnitPrice.length;
  // 2. 포인트당 원하는 width(px)
  const widthPerDataPoint = 32;
  // 3. 전체 차트 width 계산
  const chartWidth = Math.max(numDataPoints * widthPerDataPoint, 600);

  return (
    <RealChart
      charts={charts}
      chartWidth={chartWidth}
      currentDate={currentDate}
      currentMembersInnerTab={currentMembersInnerTab}
    />
  );
};
